import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { useQuery } from "@apollo/client/react";

import labels from "../../../../constants/displayFields";
import { ASSET_IN_CART_QUANTITY_DATA_QUERY } from "../../../../constants/graphql/queries";
import { REMOVE_ASSET_IN_CART_QUANTITY_MUTATION } from "../../../../constants/graphql/mutations";
import useMutation from "../../../../hooks/useMutation";
import { calculateUserQuantity } from "./utils/userQuantityCalculator";
import ConfirmDeleteModal from "src/components/images/ConfirmDeleteModal";

const styles = {
  contentRoot: {
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
};

const getDisplayName = user => {
  if (user.email && user.email.includes("@glimpse-guest.com")) {
    return "Guest User";
  }
  return user.email;
};

export default function InCartQuantityInfoModal({ asset, onClose }) {
  const [userToRemove, setUserToRemove] = useState(null);
  const { data, loading, error, refetch } = useQuery(ASSET_IN_CART_QUANTITY_DATA_QUERY, {
    variables: { assetId: asset?.id },
    skip: !asset?.id,
  });

  const [removeAssetInCartQuantity, { loading: removing }] = useMutation(
    REMOVE_ASSET_IN_CART_QUANTITY_MUTATION,
    {
      onSuccess: () => {
        refetch();
        setUserToRemove(null);
      },
      onFailure: error => {
        console.error("Failed to remove user from cart:", error);
        setUserToRemove(null);
      },
    }
  );

  const handleShowConfirmation = user => {
    setUserToRemove(user);
  };

  const handleConfirmRemove = () => {
    if (userToRemove) {
      removeAssetInCartQuantity({
        variables: {
          assetId: asset.id,
          userId: userToRemove.id,
        },
      });
    }
  };

  const handleCancelRemove = () => {
    setUserToRemove(null);
  };

  const inCartData = data?.assetInCartQuantityData;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="in-cart-quantity-info-modal"
    >
      <DialogTitle id="in-cart-quantity-info-modal">
        <Typography fontSize={18} fontWeight={500} color="text.primary" align="center">
          {labels.in_cart_quantity}
        </Typography>
        <IconButton onClick={onClose} size="large" sx={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.contentRoot}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load in-cart quantity data. Please try again.
          </Alert>
        ) : inCartData && inCartData.users && inCartData.users.length > 0 ? (
          <Grid container spacing={1} alignItems="center" sx={{ mt: 2 }}>
            {inCartData.users.map((user, userIndex) => {
              const userQuantity = calculateUserQuantity(user);
              return (
                <React.Fragment key={userIndex}>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <TextField label="User Email" value={getDisplayName(user)} disabled fullWidth />
                  </Grid>
                  <Grid size={{ xs: 8, sm: 2 }}>
                    <TextField label="Quantity" value={userQuantity} disabled fullWidth />
                  </Grid>
                  <Grid display="flex" justifyContent="center" size={{ xs: 4, sm: 2 }}>
                    <IconButton
                      onClick={() => handleShowConfirmation(user)}
                      size="small"
                      sx={{ px: 0.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        ) : (
          <Typography color="text.secondary" align="center" pt={4}>
            No users currently have this asset in their cart.
          </Typography>
        )}
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            disabled={removing}
            startIcon={removing && <CircularProgress size={20} />}
            variant="outlined"
            color="primary"
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
      {userToRemove && (
        <ConfirmDeleteModal
          title="Remove Quantity"
          descr={`Are you sure you want to remove the in cart quantity for ${getDisplayName(
            userToRemove
          )}?`}
          onClose={handleCancelRemove}
          onDelete={handleConfirmRemove}
        />
      )}
    </Dialog>
  );
}
